import {
  CouponDiscountResponse,
  CreditDiscountResponse,
  ErpOrderDiscountedPrice,
  ErpFrame,
  GiftCardDiscountResponse,
  LensCustomization,
  OrderConfigResponse,
  PaymentMethod,
  ReferralDiscountResponse,
  refractionActionSchema,
  PaymentStatus,
  PaymentProvider,
  FrameShape,
  ErpTemple,
  PromotionDiscountResponse,
} from '@ca/common'
import {
  BundleType,
  DiscountConfiguration,
  PackageType,
  StrapiID,
} from '@ca/common/types'
import { FaceData } from '@modules/brillen-sdk/src/BrillenSdk.types'
import { z } from 'zod'
import { PromotionRequirement } from './utils/summary/validatePromotions'
import { VariableValues } from './utils/OrderVariables'

/**
 * This is kept in history as stringified state so its shortened to save characters
 */
const selectedItemSchema = z.object({
  /**
   * Option id
   */
  oId: z.string(),
  /**
   * Variant id
   */
  vId: z.string().optional(),
  /**
   * Option value
   */
  val: z.string().optional(),
  /**
   * Variable name
   */
  var: z.string().optional(),
  /**
   * Overrides array of [variable, value] pairs
   */
  o: z.array(z.tuple([z.string(), z.string()])).optional(),
  /**
   * Refraction action
   */
  action: refractionActionSchema.optional(),
})

export type SelectedItem = z.infer<typeof selectedItemSchema>

export const stepsStateSchema = z.array(selectedItemSchema)

export type StepsState = z.infer<typeof stepsStateSchema>

export type StepMode = 'bundle' | 'frame'

export const CustomOptionPrefix = 'custom:'

export type OrderFrame = {
  measurement: FaceData['faceMeasurements'] | null
  shape: FrameShape
} & ErpFrame

export type Bundle = {
  type: BundleType
  steps: StepsState
}

// For compatibility
export { BundleType }

export type OrderItem = {
  id: string
  bundleType: BundleType
  frame?: OrderFrame
  // TODO: Consider moving steps state out of item so every update of steps state
  // will not rerender everything that depends on the order items
  temple?: ErpTemple
  // steps: StepsState
  // Per item flow
  flowName?: string
  isCompleted: boolean
  customization?: LensCustomization
}

export type PromotionConfig = {
  id: StrapiID
  code: string
  type: 'promotion' | 'coupon'
  // All discounts that apply to this promotion
  discounts: DiscountConfiguration[]
  // All requirements that need to be met for this promotion to apply
  requirements: PromotionRequirement[]
}
// TODO: Expand this type
export type PaymentConfig = {
  method: PaymentMethod
  amount: number
  reader_id?: number | string | null
  status: PaymentStatus
  paymentId?: string | number
  provider?: PaymentProvider
}

// For compatibility
export { PaymentStatus }

export type SelectedPackage = {
  id: StrapiID
  variantId: StrapiID | undefined
  name: string
  lensPrefix: string
  type: PackageType
  price: number
  frames: number
  color?: string | null
  value: string
  overrides: VariableValues
}

export type OrderResultActions = {
  setResult: (result: OrderConfigResponse) => void
  applyDiscountedOrderPrices: (orders: ErpOrderDiscountedPrice[]) => void
  clearFinalPrices: (orderIds: number[]) => void
  invalidateResult: () => void
}

export type CouponDiscountOption = CouponDiscountResponse & {
  id: string
}

export type ReferralDiscountOption = ReferralDiscountResponse & {
  id: string
}

export type CreditDiscountOption = CreditDiscountResponse & {
  id: string
}

export type GiftCardDiscountOption = GiftCardDiscountResponse & {
  id: string
}

export type PromotionDiscountOption = PromotionDiscountResponse & {
  id: string
}
export type DiscountOption =
  | CouponDiscountOption
  | ReferralDiscountOption
  | CreditDiscountOption
  | GiftCardDiscountOption
  | PromotionDiscountOption

export type OrderRequirements = {
  customerDetails: boolean
  invoice: boolean
}
